import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';

/**
 * @param {Object} props - Component props
 * @param {string} props.initialQuery - Initial search query
 * @param {Function} props.onSearch - Search callback
 * @param {string} props.initialType - Initial search type
 * @param {Object} props.initialFilters - Initial filters
 * @returns {JSX.Element} Enhanced SearchBar component
 */
const SearchBar = ({
  initialQuery = '',
  onSearch,
  initialType = '',
  initialFilters = {},
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState(initialType);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: initialFilters.sortBy || 'relevance',
    // User specific filters
    userLocation: initialFilters.userLocation || '',
    userSkills: initialFilters.userSkills || '',
    // Post specific filters
    postDate: initialFilters.postDate || '',
    hasTags: initialFilters.hasTags || '',
    // Community specific filters
    communityCategory: initialFilters.communityCategory || '',
    // Common filter
    createdAfter: initialFilters.createdAfter || '',
    createdBefore: initialFilters.createdBefore || '',
  });
  
  const filtersRef = useRef(null);
  
  // Close filters dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowAdvancedFilters(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filtersRef]);
  
  // Update filters based on search type
  useEffect(() => {
    if (searchType === 'users') {
      // Focus on user-specific filters
      setFilters(prev => ({
        ...prev,
        sortBy: prev.sortBy || 'relevance'
      }));
    } else if (searchType === 'posts') {
      // Focus on post-specific filters
      setFilters(prev => ({
        ...prev,
        sortBy: prev.sortBy || 'recent'
      }));
    } else if (searchType === 'communities') {
      // Focus on community-specific filters
      setFilters(prev => ({
        ...prev,
        sortBy: prev.sortBy || 'members'
      }));
    }
  }, [searchType]);
  
  // Handle query change
  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };
  
  // Handle type change
  const handleTypeChange = (e) => {
    setSearchType(e.target.value);
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Toggle advanced filters
  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(prev => !prev);
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      sortBy: 'relevance',
      userLocation: '',
      userSkills: '',
      postDate: '',
      hasTags: '',
      communityCategory: '',
      createdAfter: '',
      createdBefore: '',
    });
  };
  
  // Handle search submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!query.trim()) return;
    
    const activeFilters = Object.entries(filters)
      .filter(([_, value]) => value)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    
    if (onSearch) {
      onSearch(query, searchType, activeFilters);
    } else {
      // Navigate to search page with query parameters
      const params = new URLSearchParams();
      params.append('q', query.trim());
      
      if (searchType) {
        params.append('type', searchType);
      }
      
      // Add active filters to URL
      Object.entries(activeFilters).forEach(([key, value]) => {
        params.append(key, value);
      });
      
      navigate(`/search?${params.toString()}`);
    }
    
    // Hide advanced filters after search
    setShowAdvancedFilters(false);
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    setQuery('');
  };
  
  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center">
            <div className="relative flex-1">
              <input
                type="text"
                value={query}
                onChange={handleQueryChange}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search for people, posts, communities..."
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  className="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              
              {query.length > 0 && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Clear search</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            
            {/* Search type selector */}
            <div className="ml-2">
              <select
                value={searchType}
                onChange={handleTypeChange}
                className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="users">People</option>
                <option value="posts">Posts</option>
                <option value="communities">Communities</option>
              </select>
            </div>
            
            {/* Filter button */}
            <div className="ml-2">
              <button
                type="button"
                onClick={toggleAdvancedFilters}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${showAdvancedFilters ? 'bg-gray-100' : ''}`}
              >
                <svg 
                  className="mr-2 h-4 w-4 text-gray-500" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </button>
            </div>
            
            {/* Search button */}
            <div className="ml-2">
              <Button type="submit" variant="primary">
                Search
              </Button>
            </div>
          </div>
          
          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div 
              ref={filtersRef}
              className="mt-2 p-4 bg-white border border-gray-200 rounded-md shadow-md"
            >
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Advanced Filters</h3>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm text-blue-500 hover:text-blue-700"
                >
                  Reset All
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Sort options - always visible */}
                <div>
                  <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700">
                    Sort By
                  </label>
                  <select
                    id="sortBy"
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="relevance">Relevance</option>
                    <option value="recent">Most Recent</option>
                    {searchType === 'users' && (
                      <>
                        <option value="followers">Most Followers</option>
                        <option value="activity">Most Active</option>
                      </>
                    )}
                    {searchType === 'posts' && (
                      <>
                        <option value="likes">Most Likes</option>
                        <option value="comments">Most Comments</option>
                      </>
                    )}
                    {searchType === 'communities' && (
                      <>
                        <option value="members">Most Members</option>
                        <option value="activity">Most Active</option>
                      </>
                    )}
                  </select>
                </div>
                
                {/* Date range filters - common for all */}
                <div>
                  <label htmlFor="createdAfter" className="block text-sm font-medium text-gray-700">
                    Created After
                  </label>
                  <input
                    type="date"
                    id="createdAfter"
                    name="createdAfter"
                    value={filters.createdAfter}
                    onChange={handleFilterChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="createdBefore" className="block text-sm font-medium text-gray-700">
                    Created Before
                  </label>
                  <input
                    type="date"
                    id="createdBefore"
                    name="createdBefore"
                    value={filters.createdBefore}
                    onChange={handleFilterChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                {/* User-specific filters */}
                {(searchType === 'users' || searchType === '') && (
                  <>
                    <div>
                      <label htmlFor="userLocation" className="block text-sm font-medium text-gray-700">
                        Location
                      </label>
                      <input
                        type="text"
                        id="userLocation"
                        name="userLocation"
                        value={filters.userLocation}
                        onChange={handleFilterChange}
                        placeholder="e.g. New York, Remote"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="userSkills" className="block text-sm font-medium text-gray-700">
                        Skills
                      </label>
                      <input
                        type="text"
                        id="userSkills"
                        name="userSkills"
                        value={filters.userSkills}
                        onChange={handleFilterChange}
                        placeholder="e.g. React, JavaScript"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}
                
                {/* Post-specific filters */}
                {(searchType === 'posts' || searchType === '') && (
                  <>
                    <div>
                      <label htmlFor="postDate" className="block text-sm font-medium text-gray-700">
                        Time Frame
                      </label>
                      <select
                        id="postDate"
                        name="postDate"
                        value={filters.postDate}
                        onChange={handleFilterChange}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                      >
                        <option value="">Any Time</option>
                        <option value="day">Last 24 Hours</option>
                        <option value="week">Last Week</option>
                        <option value="month">Last Month</option>
                        <option value="year">Last Year</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="hasTags" className="block text-sm font-medium text-gray-700">
                        Has Tags
                      </label>
                      <input
                        type="text"
                        id="hasTags"
                        name="hasTags"
                        value={filters.hasTags}
                        onChange={handleFilterChange}
                        placeholder="e.g. help, question"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}
                
                {/* Community-specific filters */}
                {(searchType === 'communities' || searchType === '') && (
                  <div>
                    <label htmlFor="communityCategory" className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <select
                      id="communityCategory"
                      name="communityCategory"
                      value={filters.communityCategory}
                      onChange={handleFilterChange}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                      <option value="">All Categories</option>
                      <option value="technology">Technology</option>
                      <option value="business">Business</option>
                      <option value="health">Health & Fitness</option>
                      <option value="education">Education</option>
                      <option value="entertainment">Entertainment</option>
                      <option value="lifestyle">Lifestyle</option>
                      <option value="social">Social</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button 
                  type="submit" 
                  variant="primary"
                  size="sm"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

SearchBar.propTypes = {
  initialQuery: PropTypes.string,
  onSearch: PropTypes.func,
  initialType: PropTypes.string,
  initialFilters: PropTypes.object,
};

export default SearchBar;